import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { adminApi, formatApiErrorDetail } from "../lib/api";
import { NaaviLogo, ShapeSquare, ShapeCircle } from "../components/brand/Shapes";
import { ShieldCheck, ArrowLeft, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = params.get("token");
    if (t) setToken(t);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await adminApi.post("/admin/reset-password", { token: token.trim(), new_password: password });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/admin/login");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] relative overflow-hidden flex items-center justify-center px-5 py-10" data-testid="admin-reset-password-page">
      <ShapeSquare size={220} className="absolute -top-16 -left-16 opacity-90 rotate-12" color="#648DE5" />
      <ShapeCircle size={140} className="absolute bottom-10 right-10 opacity-90" color="#C6D947" />

      <div className="relative w-full max-w-md">
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-sm text-[#0F1B47] mb-6 font-semibold">
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="bg-[#FDFDF9] rounded-[32px] p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(15,27,71,0.25)] border border-[#0F1B47]/8">
          <div className="flex items-center gap-3 mb-6">
            <NaaviLogo height={36} />
          </div>
          <div className="inline-flex items-center gap-2 bg-[#F5F5F0] rounded-full px-3 py-1.5 mb-5">
            <ShieldCheck size={13} className="text-[#3AB0D6]" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#0F1B47]">Admin console</span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0F1B47] leading-tight mb-2">Set a new password.</h1>
          <p className="text-[#4A5568] text-sm font-light mb-8">
            Use a strong password — at least 8 characters with a mix of letters, numbers and symbols.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-reset-form">
            <Field label="One-time reset code">
              <div className="relative">
                <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0F1B47]/50" />
                <input
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste from the email"
                  required
                  className={`${inputCls} pl-11 font-mono text-sm`}
                  data-testid="admin-reset-token"
                />
              </div>
            </Field>
            <Field label="New password">
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  className={inputCls}
                  data-testid="admin-reset-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#0F1B47]/60"
                  aria-label="Toggle password visibility"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            <Field label="Confirm new password">
              <input
                type={showPwd ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again"
                required
                minLength={8}
                className={inputCls}
                data-testid="admin-reset-confirm"
              />
            </Field>

            {error && (
              <div className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="admin-reset-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] disabled:opacity-60"
              data-testid="admin-reset-submit"
            >
              {loading ? "Updating…" : "Update password & sign in"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#0F1B47]/8">
            <p className="text-xs text-[#718096] font-light">
              Need a new code?{" "}
              <Link to="/admin/forgot-password" className="font-semibold text-[#0F1B47] underline underline-offset-2">Send another reset email →</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
